/*
 * Results screen — port of HTML showResults() / buildPlanMarkdown().
 *
 * Renders:
 *   - Stage badge + alignment-gap banner (when gap1 ≤ 2)
 *   - Part 1 "Leverage points" cards (Map the System, Target Constraint, Bright Spots, Goal of the Goal)
 *   - Part 2 "Resource stack" — top-N matched action cards + Do-Less-Do-More reflection
 *   - Pre-mortem card (when gap3 set)
 *   - Joy-in-Work bars (collapsed, supporting detail)
 *   - Print / Copy / Start over actions
 */
import { useMemo, useRef, useState, useCallback } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { Button } from '@shared/components';
import type { SharedLens } from '@shared/lens';
import {
  scoreAll,
} from '../scoring';
import {
  matchActions,
} from '../plan-generation';
import { buildPlanMarkdown } from '../plan-markdown';
import type { DiagnosticAnswers } from '../types';
import { usePlanBuilderConfigContext } from '../PlanBuilderConfigContext';
import { CaptureCard } from './CaptureCard';
import { EmailPlanForm, EmailPlanToggle } from './EmailPlanPanel';

interface Props {
  lens: SharedLens;
  answers: DiagnosticAnswers;
  onRetake(): void;
  submissionId?: string | null;
  mode?: 'live' | 'fetched';
}

export function ResultsPage({ lens, answers, onRetake, submissionId, mode = 'live' }: Props) {
  const config = usePlanBuilderConfigContext();
  const score = useMemo(() => scoreAll(answers, config), [answers, config]);
  const matched = useMemo(
    () => matchActions(score, answers, config, { limit: 4 }),
    [score, answers, config],
  );

  const [copied, setCopied] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const blocker = answers.gap2 ? config.planEngine.blockers[answers.gap2] : null;

  const miracleAnswers = (
    ['miracle_desk', 'miracle_midday', 'miracle_gc'] as const
  )
    .map((k) => answers[k] && String(answers[k]).trim())
    .filter((s): s is string => Boolean(s));

  const buildMarkdown = useCallback(
    () => buildPlanMarkdown(score, answers, matched, config),
    [score, answers, matched, config],
  );

  const handleCopy = async () => {
    const md = buildMarkdown();
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback — buyer can select-and-copy from a temporary textarea.
      window.prompt('Copy plan', md);
    }
  };

  const resetLevers = config.planEngine.resetLevers;

  return (
    <div className="pb-results-wrap">
      <div className="pb-print-header" aria-hidden="true">
        <div className="pb-print-header-row">
          <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: '0.4px' }}>
            mot-r
          </div>
          <div className="pb-print-header-url">www.mot-r.com</div>
        </div>
        <div className="pb-print-header-tagline">
          Legal Operations Improvement Plan
        </div>
      </div>

      {mode === 'live' && (
        <div className="pb-disclosure-banner no-print">
          <svg
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <strong>About these results.</strong> {config.results.disclosureLive}{' '}
            <PrivacyNoticeLink />.
          </div>
        </div>
      )}
      {mode === 'fetched' && (
        <div className="pb-disclosure-banner no-print">
          <svg
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <strong>You're viewing a saved plan.</strong> {config.results.disclosureFetched}{' '}
            <a href="/plan-builder" className="pb-privacy-link">Start a new plan</a>.
          </div>
        </div>
      )}

      <div className="pb-results-header">
        <div className={`pb-stage-badge s${score.stage}`}>
          <span className="pb-badge-num">{score.stage}</span>
          <span>
            Stage {score.stage} — {score.stageData.name}
          </span>
        </div>
        <h2 className="pb-results-title">
          Here's your plan, calibrated to a{' '}
          <span style={{ color: 'var(--primary)' }}>
            {score.stageData.name.toLowerCase()}
          </span>{' '}
          team.
        </h2>
        <p className="pb-results-sub">
          A Reset-shaped plan — first the leverage points (<em>where</em> to
          push), then the resource stack (<em>how</em> to push). Grounded in
          Heath's <em>Reset</em> methodology (2025), IHI Joy in Work (2017),
          and Klein's Pre-Mortem (2007).
        </p>
      </div>

      {score.alignmentGap && (
        <div className="pb-alignment-banner">
          <svg
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.008v.008H12v-.008z"
            />
          </svg>
          <div>
            <strong>{config.results.alignmentGapHeading}</strong> {config.results.alignmentGapBody}
          </div>
        </div>
      )}

      {/* Part 1 — Leverage points */}
      <section className="pb-reset-part">
        <div className="pb-reset-part-header">
          <span className="pb-reset-part-label">{config.results.part1Label}</span>
          <h3 className="pb-reset-part-title">{config.results.part1Title}</h3>
          <p className="pb-reset-part-sub">
            {config.results.part1Sub}
          </p>
        </div>
        <div className="pb-leverage-grid">
          <div className="pb-leverage-card">
            <span className="pb-lever-tag part-1">
              {resetLevers.map_the_system.name}
            </span>
            <div className="pb-leverage-title">
              The system is at{' '}
              <em>
                Stage {score.stage} — {score.stageData.name}
              </em>
            </div>
            <div className="pb-leverage-body">{score.stageData.desc}</div>
          </div>
          {blocker && (
            <div className="pb-leverage-card">
              <span className="pb-lever-tag part-1">
                {resetLevers.target_constraint.name}
              </span>
              <div className="pb-leverage-title">
                The #1 constraint is <em>{blocker.label}</em>
              </div>
              <div className="pb-leverage-body">{blocker.body}</div>
            </div>
          )}
          <div className="pb-leverage-card">
            <span className="pb-lever-tag part-1">
              {resetLevers.bright_spots.name}
            </span>
            <div className="pb-leverage-title">
              Your brightest spot is <em>{config.scoring.ihiLabels[score.strongest]}</em>
            </div>
            <div className="pb-leverage-body">
              {config.results.brightsSpotBody}
            </div>
          </div>
        </div>
        {/* Goal of the Goal — quotes back the miracle answers if any */}
        <div className="pb-reflection-prompt">
          <span className="pb-lever-tag part-1">
            {resetLevers.goal_of_goal.name}
            {miracleAnswers.length > 0 ? " — what you're moving toward" : ''}
          </span>
          {miracleAnswers.length === 0 && (
            <>
              <h4>{config.results.goalOfGoalHeading}</h4>
              <p>
                {config.results.goalOfGoalBody}
              </p>
            </>
          )}
          {miracleAnswers.length > 0 && (
            <>
              <h4>{config.results.miracleHeading}</h4>
              {miracleAnswers.map((q, i) => (
                <div key={i} className="pb-miracle-quote">
                  {q}
                </div>
              ))}
              <p style={{ marginTop: 12 }}>
                {config.results.miracleSuffix}
              </p>
            </>
          )}
        </div>
      </section>

      {/* Part 2 — Resource stack */}
      <section className="pb-reset-part">
        <div className="pb-reset-part-header">
          <span className="pb-reset-part-label">{config.results.part2Label}</span>
          <h3 className="pb-reset-part-title">{config.results.part2Title}</h3>
          <p className="pb-reset-part-sub">
            {config.results.part2Sub.replace('{n}', String(matched.length))}
          </p>
        </div>
        {matched.map((m, i) => {
          const a = m.action;
          const lever = resetLevers[a.resetLever];
          const effortLabel =
            a.effort === 'low' ? 'Low effort' : a.effort === 'high' ? 'High effort' : 'Medium effort';
          return (
            <div key={a.id} className={`pb-action-card${i === 0 ? ' primary' : ''}`}>
              <div className="pb-action-number">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="pb-action-card-header">
                <span className={`pb-lever-tag part-${lever.part}`}>{lever.name}</span>
                <span className="pb-action-meta">
                  {effortLabel}
                  <span className="dot" />
                  {a.horizon}
                </span>
              </div>
              <div className="pb-action-title">{a.title}</div>
              <div className="pb-action-why">
                <svg
                  width="12"
                  height="12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>Why this, for your team: {m.reasons.join(' · ')}.</span>
              </div>
              <div className="pb-action-body">{a.body}</div>
              <div className="pb-action-research">
                <strong>Evidence:</strong> {a.research}
              </div>
            </div>
          );
        })}
        <div className="pb-reflection-prompt">
          <span className="pb-lever-tag part-2">
            {resetLevers.do_less_do_more.name}
          </span>
          <h4>{config.results.doLessHeading}</h4>
          <p>
            {config.results.doLessBody}
          </p>
        </div>

        {/* Joy in Work profile — collapsed, supporting detail */}
        <details className="pb-supporting-detail">
          <summary>
            {config.results.joyProfileSummary} ({score.joyScore}/100)
          </summary>
          <div style={{ paddingTop: 12 }}>
            {(Object.entries(score.driverScores) as Array<[keyof typeof score.driverScores, number]>).map(
              ([d, val]) => {
                const pct = ((val - 1) / 3) * 100;
                const fill =
                  val <= 1.5
                    ? 'var(--error)'
                    : val <= 2.5
                    ? 'var(--warning)'
                    : val <= 3.5
                    ? 'var(--accent)'
                    : 'var(--success)';
                const isWeakest = score.weakest.includes(d);
                return (
                  <div key={d} className="pb-drow">
                    <span className="pb-dlabel">
                      {config.scoring.ihiLabels[d]}
                      {isWeakest && (
                        <span
                          className="pb-priority-arrow"
                          title="Priority area"
                        >
                          ↓
                        </span>
                      )}
                    </span>
                    <div className="pb-dtrack">
                      <div
                        className="pb-dfill"
                        style={{ width: `${pct}%`, background: fill }}
                      />
                    </div>
                    <span className="pb-dscore">{val}/4</span>
                  </div>
                );
              },
            )}
            <p className="pb-joy-footnote">
              {config.results.joyFootnote}
            </p>
          </div>
        </details>
      </section>

      {score.preMortem && config.planEngine.premortems[score.preMortem] && (
        <div className="pb-pm-card">
          <div className="pb-pm-header">
            <div className="pb-pm-icon" aria-hidden="true">
              <svg
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <div>
              <div className="pb-pm-htitle">
                {config.results.preMortemTitle}
              </div>
              <div className="pb-pm-hsub">
                {config.results.preMortemSub}
              </div>
            </div>
          </div>
          <div className="pb-pm-body">
            <div className="pb-pm-label">Identified risk</div>
            <div className="pb-pm-risk">{config.planEngine.premortems[score.preMortem].risk}</div>
            <div className="pb-pm-label">How to address it</div>
            <div className="pb-pm-mitigation">{config.planEngine.premortems[score.preMortem].mit}</div>
          </div>
        </div>
      )}

      {submissionId && <ShareLinkBlock submissionId={submissionId} />}

      {mode === 'live' && <CaptureCard lens={lens} answers={answers} score={score} />}

      <div className="pb-share-card no-print">
        <div className="pb-share-title">{config.results.shareTitle}</div>
        <p className="pb-share-body">
          {config.results.shareBody}
        </p>
        <div className="pb-share-actions">
          <Button onClick={() => window.print()}>
            Print / save as PDF
          </Button>
          <Button variant="secondary" onClick={handleCopy}>
            {copied ? 'Copied to clipboard' : 'Copy plan (Markdown)'}
          </Button>
          {submissionId && (
            <EmailPlanToggle
              open={emailOpen}
              onToggle={() => setEmailOpen((v) => !v)}
            />
          )}
          <Button variant="ghost" onClick={onRetake}>
            Start over
          </Button>
        </div>
        {submissionId && emailOpen && (
          <EmailPlanForm
            submissionId={submissionId}
            buildMarkdown={buildMarkdown}
            onClose={() => setEmailOpen(false)}
          />
        )}
      </div>

      {mode === 'live' && (
        <p className="pb-results-footnote">
          {config.results.footnoteLive}
        </p>
      )}
    </div>
  );
}

function ShareLinkBlock({ submissionId }: { submissionId: string }) {
  const baseUrl =
    (import.meta.env.VITE_APP_BASE_URL as string | undefined) || window.location.origin;
  const shareUrl = `${baseUrl.replace(/\/$/, '')}/plan-builder/results/${submissionId}`;
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy your plan link', shareUrl);
    }
  }, [shareUrl]);
  return (
    <div className="pb-share-link no-print">
      <div className="pb-share-link-label">Your plan is at</div>
      <div className="pb-share-link-row">
        <input
          readOnly
          className="pb-share-link-url"
          value={shareUrl}
          aria-label="Shareable plan link"
          onClick={(e) => (e.target as HTMLInputElement).select()}
        />
        <button
          type="button"
          className="pb-share-link-copy"
          onClick={copy}
          aria-label={copied ? 'Link copied' : 'Copy share link'}
        >
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
      <div className="pb-share-link-hint">
        Bookmark or share — anyone with the link can revisit this plan.
      </div>
    </div>
  );
}

// Router-aware privacy link: <Link> in the SWA build (SPA navigation),
// plain <a> in the Squarespace single-file build (no router available).
function PrivacyNoticeLink() {
  const inRouter = useInRouterContext();
  if (inRouter) {
    return (
      <Link to="/privacy" className="pb-privacy-link">
        See our privacy notice
      </Link>
    );
  }
  return (
    <a href="/privacy" className="pb-privacy-link">
      See our privacy notice
    </a>
  );
}
